import { useMemo } from 'react';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { useEstoqueContext } from '../Context';
import { formatQuantity } from '../units';
import { ChevronRight, PackageOpen, TriangleAlert } from 'lucide-react';

export default function EstoqueOverview() {
    const {
        summary,
        summaryLoading,
        recommendations,
        recommendationsLoading,
        overviewError,
        reloadSummary,
        reloadRecommendations,
    } = useEstoqueContext();
    const { setParams } = useParamsContext();

    const criticalItems = useMemo(() => {
        const items = summary?.criticalItems || [];
        return [...items].sort((a: any, b: any) => (a?.onHand ?? 0) - (b?.onHand ?? 0));
    }, [summary]);

    const purchaseSuggestions = useMemo(
        () => (recommendations || []).filter((r: any) => (r?.suggestedPurchase ?? 0) > 0),
        [recommendations],
    );

    if ((summaryLoading && !summary) || (recommendationsLoading && !recommendations.length)) {
        return (
            <div className="space-y-2" aria-busy="true">
                <div className="sf-skeleton h-24" />
                <div className="sf-skeleton h-32" />
            </div>
        );
    }

    if (overviewError && !summary) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar o estoque</h2>
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                        void reloadSummary();
                        void reloadRecommendations();
                    }}
                >
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!summary?.totalItems) {
        return (
            <div className="sf-state">
                <PackageOpen aria-hidden />
                <h2>Estoque vazio</h2>
                <p>Cadastre produtos e insumos para acompanhar o saldo e receber alertas de compra.</p>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setParams({ action: 'items', id: '' })}>
                    Cadastrar itens
                </button>
            </div>
        );
    }

    return (
        <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
            <section className="space-y-6">
                <div>
                    <h2 className="sf-group-title">Resumo</h2>
                    <ul className="sf-group">
                        <li>
                            <button type="button" className="sf-row" onClick={() => setParams({ action: 'items', id: '' })}>
                                <span className="sf-row-body text-sm text-base-content">Itens cadastrados</span>
                                <span className="sf-row-end">{summary?.totalItems ?? 0}</span>
                                <ChevronRight className="sf-chevron" aria-hidden />
                            </button>
                        </li>
                        <li>
                            <div className="sf-row">
                                <span className="sf-row-body text-sm text-base-content">Itens críticos</span>
                                <span className={`sf-row-end ${criticalItems.length ? '!text-error' : ''}`}>
                                    {summary?.criticalCount ?? 0}
                                </span>
                            </div>
                        </li>
                        <li>
                            <button type="button" className="sf-row" onClick={() => setParams({ action: 'movement', id: '' })}>
                                <span className="sf-row-body text-sm font-medium !text-primary">Registrar movimentação</span>
                                <ChevronRight className="sf-chevron" aria-hidden />
                            </button>
                        </li>
                    </ul>
                </div>

                <div>
                    <h2 className="sf-group-title">Itens críticos</h2>
                    {criticalItems.length === 0 ? (
                        <p className="px-1 text-sm text-base-content/55">Nenhum item abaixo do mínimo.</p>
                    ) : (
                        <ul className="sf-group">
                            {criticalItems.map((item: any) => (
                                <li key={item.itemId}>
                                    <button
                                        type="button"
                                        className="sf-row"
                                        onClick={() => setParams({ action: 'detail', id: item.itemId })}
                                    >
                                        <span className="sf-row-body">
                                            <span className="sf-row-title">{item.name}</span>
                                            <span className="sf-row-sub">
                                                Mínimo {item.minStock ?? '—'} · Reposição {item.rop ?? '—'}
                                            </span>
                                        </span>
                                        <span className="sf-row-end !text-error">{formatQuantity(item.onHand)}</span>
                                        <ChevronRight className="sf-chevron" aria-hidden />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            <section>
                <h2 className="sf-group-title">Sugestão de compra</h2>
                {purchaseSuggestions.length === 0 ? (
                    <p className="px-1 text-sm text-base-content/55">Nenhuma compra sugerida no momento.</p>
                ) : (
                    <ul className="sf-group">
                        {purchaseSuggestions.map((item: any) => (
                            <li key={item.itemId}>
                                <button
                                    type="button"
                                    className="sf-row"
                                    onClick={() => setParams({ action: 'detail', id: item.itemId })}
                                >
                                    <span className="sf-row-body">
                                        <span className="sf-row-title">{item.name}</span>
                                        <span className="sf-row-sub">
                                            Saldo {formatQuantity(item.onHand)} · Consumo de{' '}
                                            {formatQuantity(item.averageDailyConsumption)} por dia
                                        </span>
                                    </span>
                                    <span className="sf-row-end">Comprar {Math.ceil(item.suggestedPurchase ?? 0)}</span>
                                    <ChevronRight className="sf-chevron" aria-hidden />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
